import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import {
  ArrowLeft, FileText, CheckCircle, Calendar, User, ChevronRight, Loader2, Star, Sparkles
} from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ClientReportsListPage = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [reports, setReports] = useState([]);

  const fetchReports = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/clients/me/reports`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReports(res.data);
    } catch (err) {
      toast.error('Failed to load reports');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  const getScoreColor = (score) => {
    if (score <= 40) return 'bg-red-100 text-red-600';
    if (score <= 65) return 'bg-amber-100 text-amber-600';
    if (score <= 85) return 'bg-green-100 text-green-600';
    return 'bg-emerald-100 text-emerald-600';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-zinc-50 to-zinc-100">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-sm border-b border-zinc-200 sticky top-0 z-40">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/portal')} data-testid="back-btn">
            <ArrowLeft className="h-4 w-4 mr-2" /> Back to Portal
          </Button>
          <div>
            <h1 className="text-xl font-bold text-zinc-900">My Performance Reports</h1>
            <p className="text-sm text-zinc-500">View your cleaning history and results</p>
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 py-6">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-[#F56F05]" />
          </div>
        ) : reports.length === 0 ? (
          <Card className="bg-white">
            <CardContent className="py-16 text-center">
              <FileText className="h-16 w-16 mx-auto text-zinc-200 mb-4" />
              <h2 className="text-xl font-semibold text-zinc-700 mb-2">No Reports Yet</h2>
              <p className="text-zinc-500 mb-6">
                Performance reports will appear here after your cleaning appointments are completed.
              </p>
              <Button onClick={() => navigate('/booking')} className="bg-[#F56F05] hover:bg-[#C2410C]">
                Book Your First Cleaning
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {/* Summary Stats */}
            {reports.length >= 2 && (
              <Card className="bg-gradient-to-r from-[#F56F05] to-orange-400 text-white mb-8">
                <CardContent className="py-6">
                  <div className="grid grid-cols-3 gap-6 text-center">
                    <div>
                      <p className="text-3xl font-bold">{reports.length}</p>
                      <p className="text-sm opacity-90">Total Cleanings</p>
                    </div>
                    <div>
                      <p className="text-3xl font-bold">
                        {Math.round(reports.reduce((sum, r) => sum + r.after_score, 0) / reports.length)}
                      </p>
                      <p className="text-sm opacity-90">Average Score</p>
                    </div>
                    <div>
                      <p className="text-3xl font-bold">
                        {reports.filter(r => r.improvement_delta && r.improvement_delta > 0).length}
                      </p>
                      <p className="text-sm opacity-90">Improvements</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Reports List */}
            {reports.map((report, idx) => (
              <motion.div
                key={report.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.05 }}
              >
                <Card 
                  className="hover:shadow-lg transition-all cursor-pointer group"
                  onClick={() => navigate(`/portal/reports/${report.id}`)}
                  data-testid={`report-card-${report.id}`}
                >
                  <CardContent className="py-5">
                    <div className="flex items-center gap-4">
                      {/* Score Badge */}
                      <div className={`w-16 h-16 rounded-2xl flex items-center justify-center text-2xl font-bold ${getScoreColor(report.after_score)}`}>
                        {Math.round(report.after_score)}
                      </div>
                      
                      {/* Details */}
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold text-zinc-900">{report.service_name}</h3>
                          {report.improvement_badge && (
                            <Badge className="bg-green-100 text-green-700 text-xs">
                              <Sparkles className="h-3 w-3 mr-1" />
                              {report.improvement_badge}
                            </Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-4 mt-1 text-sm text-zinc-500">
                          <span className="flex items-center gap-1">
                            <Calendar className="h-4 w-4" />
                            {new Date(report.service_date).toLocaleDateString()}
                          </span>
                          <span className="flex items-center gap-1">
                            <User className="h-4 w-4" />
                            {report.cleaner_name}
                          </span>
                        </div>
                        {report.improvement_delta !== null && (
                          <p className={`text-sm font-medium mt-1 ${
                            report.improvement_delta >= 0 ? 'text-green-600' : 'text-red-600'
                          }`}>
                            {report.improvement_delta >= 0 ? '+' : ''}{Math.round(report.improvement_delta)} points improvement
                          </p>
                        )}
                      </div>
                      
                      {/* Arrow */}
                      <ChevronRight className="h-5 w-5 text-zinc-300 group-hover:text-[#F56F05] group-hover:translate-x-1 transition-all" />
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ClientReportsListPage;
